import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, fmtMoney, fmtDateRelative } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import PipelineAdmin from "../components/sourcing/PipelineAdmin";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    Crown, Building2, CheckCircle2, XCircle, CalendarPlus, Gift, Percent,
    CreditCard, Plus, Trash2, Users, ReceiptText, TrendingUp,
    Database, Upload, Download, RefreshCw, FileText, Loader2, AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const STATUS_BADGE = {
    trialing: "bg-gold/15 text-gold border-gold/40",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    past_due: "bg-orange-50 text-orange-700 border-orange-300",
    disabled: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-neutral-100 text-neutral-600 border-neutral-300",
    partner: "border-[#C9A227]/40 text-[#C9A227]",
};

export default function SuperAdmin() {
    const { user, ready } = useAuth();
    if (!ready) return null;
    if (user?.role !== "super_admin") return <Navigate to="/" replace />;

    return (
        <div data-testid="super-admin-page">
            <PageHeader
                title="Super-Admin Console"
                subtitle="Manage tenants, plans, discounts and billing across the platform"
            >
                <Badge className="bg-gold text-foreground border-gold/30 gap-1.5">
                    <Crown className="w-3 h-3" /> Super-Admin
                </Badge>
            </PageHeader>
            <div className="p-6 lg:p-8 space-y-6">
                <SummaryStrip />
                <Tabs defaultValue="companies">
                    <TabsList data-testid="super-admin-tabs">
                        <TabsTrigger value="companies" data-testid="tab-companies"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Companies</TabsTrigger>
                        <TabsTrigger value="plans" data-testid="tab-plans"><CreditCard className="w-3.5 h-3.5 mr-1.5" /> Plans</TabsTrigger>
                        <TabsTrigger value="discounts" data-testid="tab-discounts"><Percent className="w-3.5 h-3.5 mr-1.5" /> Discounts</TabsTrigger>
                        <TabsTrigger value="transactions" data-testid="tab-transactions"><ReceiptText className="w-3.5 h-3.5 mr-1.5" /> Transactions</TabsTrigger>
                        <TabsTrigger value="ch-data" data-testid="tab-ch-data"><Database className="w-3.5 h-3.5 mr-1.5" /> CH Data</TabsTrigger>
                    </TabsList>
                    <TabsContent value="companies" className="mt-4"><CompaniesTab /></TabsContent>
                    <TabsContent value="plans" className="mt-4"><PlansTab /></TabsContent>
                    <TabsContent value="discounts" className="mt-4"><DiscountsTab /></TabsContent>
                    <TabsContent value="transactions" className="mt-4"><TransactionsTab /></TabsContent>
                    <TabsContent value="ch-data" className="mt-4 space-y-6"><PipelineAdmin /><SourcingDataTab /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function SummaryStrip() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/super-admin/summary").then((r) => setS(r.data)); }, []);
    if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;
    return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Total companies" value={s.total_companies} icon={<Building2 className="w-4 h-4 text-gold" />} testid="stat-total-companies" />
            <Stat label="Active subscriptions" value={s.active_subscriptions} testid="stat-active-subs" />
            <Stat label="MRR (est.)" value={fmtMoney(s.mrr, "GBP")} mono accent testid="stat-mrr" />
            <Stat label="Revenue this month" value={fmtMoney(s.revenue_this_month, "GBP")} mono testid="stat-revenue" />
            <Stat label="Trialing" value={s.by_status?.trialing || 0} testid="stat-trialing" />
        </div>
    );
}

function Stat({ label, value, icon, mono, accent, testid }) {
    return (
        <div className="border border-border rounded-md bg-white p-4" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                {icon} {label}
            </div>
            <div className={`mt-1 text-2xl font-bold ${mono ? "font-mono" : "font-display"} ${accent ? "text-gold" : "text-navy"}`}>{value ?? "—"}</div>
        </div>
    );
}

function CompaniesTab() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const refresh = async () => {
        const { data } = await api.get("/super-admin/companies");
        setCompanies(data);
    };
    useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

    const filtered = filter === "all" ? companies : companies.filter((c) => c.status === filter);

    if (loading) return <div className="text-sm text-muted-foreground">Loading companies…</div>;

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-2" data-testid="company-status-filters">
                {["all", "trialing", "active", "past_due", "disabled", "cancelled"].map((s) => (
                    <Button
                        key={s}
                        size="sm"
                        variant={filter === s ? "default" : "outline"}
                        onClick={() => setFilter(s)}
                        data-testid={`filter-${s}`}
                        className={filter === s ? "bg-foreground text-background" : ""}
                    >
                        {s} ({s === "all" ? companies.length : companies.filter((c) => c.status === s).length})
                    </Button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">
                    No companies in this filter.
                </div>
            ) : (
                <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="companies-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                <th className="text-left px-4 py-3">Company</th>
                                <th className="text-left px-4 py-3">Owner</th>
                                <th className="text-left px-4 py-3">Plan</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Period ends</th>
                                <th className="text-left px-4 py-3">Users</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c, i) => (
                                <motion.tr
                                    key={c.id}
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: Math.min(i * 0.02, 0.2), duration: 0.2 }}
                                    className="border-b border-border/60 last:border-0 hover:bg-muted/30"
                                    data-testid={`company-row-${c.id}`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{c.name}</div>
                                        {c.discount_percent > 0 && (
                                            <div className="text-[10px] text-gold">{c.discount_percent}% off applied</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {c.owner ? (
                                            <>
                                                <div>{c.owner.name}</div>
                                                <div className="text-muted-foreground font-mono">{c.owner.email}</div>
                                            </>
                                        ) : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-4 py-3 capitalize text-xs font-mono">
                                        {c.plan_type === "partner_comp" ? (
                                            <span
                                                data-testid={`plan-partner-${c.id}`}
                                                title={c.granted_by && c.granted_at ? `Granted by ${c.granted_by}, ${new Date(c.granted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : "Partner comp"}
                                                className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider cursor-default"
                                                style={{ background: 'rgba(201,162,39,0.12)', color: '#C9A227', border: '1px solid rgba(201,162,39,0.35)' }}
                                            >
                                                Partner (comp)
                                            </span>
                                        ) : (c.plan_slug || "—")}
                                    </td>
                                    <td className="px-4 py-3">
                                        {c.plan_type === "partner_comp" ? (
                                            <span
                                                data-testid={`status-partner-${c.id}`}
                                                title={c.granted_by && c.granted_at ? `Granted by ${c.granted_by}, ${new Date(c.granted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : "Partner comp"}
                                                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium cursor-default ${STATUS_BADGE.partner}`}
                                                style={{ background: 'rgba(201,162,39,0.1)' }}
                                            >
                                                Partner
                                            </span>
                                        ) : (
                                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${STATUS_BADGE[c.status] || ""}`}>
                                                {c.status}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                                        {c.current_period_end ? new Date(c.current_period_end).toLocaleDateString() :
                                         c.trial_ends_at ? `Trial ${new Date(c.trial_ends_at).toLocaleDateString()}` : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs"><Users className="w-3 h-3 inline mr-1 text-muted-foreground" />{c.user_count}</td>
                                    <td className="px-4 py-3 text-right">
                                        <CompanyActions company={c} onChange={refresh} />
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function CompanyActions({ company, onChange }) {
    const [open, setOpen] = useState(false);
    const [grantOpen, setGrantOpen] = useState(false);
    const [discountOpen, setDiscountOpen] = useState(false);

    const approve = async () => {
        await api.post(`/super-admin/companies/${company.id}/approve`);
        toast.success("Company approved");
        onChange();
    };
    const disable = async () => {
        if (!confirm(`Disable ${company.name}? Users won't be able to log in.`)) return;
        await api.post(`/super-admin/companies/${company.id}/disable`);
        toast.success("Company disabled");
        onChange();
    };
    const extend = async () => {
        await api.post(`/super-admin/companies/${company.id}/extend-trial?days=14`);
        toast.success("Trial extended by 14 days");
        onChange();
    };

    return (
        <div className="flex items-center gap-1 justify-end">
            {company.status !== "active" && (
                <Button size="sm" variant="outline" onClick={approve} data-testid={`approve-${company.id}`} className="h-7 text-xs gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Approve
                </Button>
            )}
            {company.status === "trialing" && (
                <Button size="sm" variant="outline" onClick={extend} data-testid={`extend-${company.id}`} className="h-7 text-xs gap-1">
                    <CalendarPlus className="w-3 h-3" /> Extend
                </Button>
            )}
            <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
                <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid={`grant-${company.id}`} className="h-7 text-xs gap-1">
                        <Gift className="w-3 h-3 text-gold" /> Grant
                    </Button>
                </DialogTrigger>
                <GrantDialog company={company} onDone={() => { setGrantOpen(false); onChange(); }} />
            </Dialog>
            <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
                <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid={`discount-${company.id}`} className="h-7 text-xs gap-1">
                        <Percent className="w-3 h-3" /> Discount
                    </Button>
                </DialogTrigger>
                <DiscountDialog company={company} onDone={() => { setDiscountOpen(false); onChange(); }} />
            </Dialog>
            {company.status !== "disabled" && company.slug !== "house" && (
                <Button size="sm" variant="ghost" onClick={disable} data-testid={`disable-${company.id}`} className="h-7 text-xs gap-1 text-red-600 hover:text-red-700">
                    <XCircle className="w-3 h-3" />
                </Button>
            )}
        </div>
    );
}

function GrantDialog({ company, onDone }) {
    const [planSlug, setPlanSlug] = useState("pro");
    const [months, setMonths] = useState(1);
    const [note, setNote] = useState("");
    const [isComp, setIsComp] = useState(false);
    const [compReason, setCompReason] = useState("");
    const [grantedBy, setGrantedBy] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        setLoading(true);
        try {
            if (isComp) {
                await api.post(`/super-admin/companies/${company.id}/partner-comp`, {
                    comp_reason: compReason || "Complimentary access",
                    granted_by: grantedBy || "Super Admin",
                    expires_at: null,
                });
                toast.success(`Partner comp granted to ${company.name} (indefinite)`);
            } else {
                await api.post(`/super-admin/companies/${company.id}/grant`, { plan_slug: planSlug, months: parseInt(months), note });
                toast.success(`Granted ${months} month${months > 1 ? "s" : ""} on ${planSlug}`);
            }
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <DialogContent data-testid="grant-dialog">
            <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2"><Gift className="w-4 h-4 text-gold" /> Grant subscription · {company.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
                {/* Partner comp toggle */}
                <label className="flex items-center gap-2.5 cursor-pointer p-3 rounded-md border border-border hover:bg-muted/40 transition-colors" data-testid="partner-comp-toggle">
                    <input
                        type="checkbox"
                        checked={isComp}
                        onChange={(e) => setIsComp(e.target.checked)}
                        className="accent-[#C9A227] w-3.5 h-3.5"
                    />
                    <div>
                        <div className="text-sm font-medium">Partner comp (no expiry)</div>
                        <div className="text-[11px] text-muted-foreground">Full Pro access, billing exempt, indefinite</div>
                    </div>
                </label>

                {isComp ? (
                    <div className="grid grid-cols-1 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Comp reason</Label>
                            <Input value={compReason} onChange={(e) => setCompReason(e.target.value)} placeholder="M&A advisory partner" data-testid="comp-reason-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Granted by</Label>
                            <Input value={grantedBy} onChange={(e) => setGrantedBy(e.target.value)} placeholder="Graham Orodje" data-testid="granted-by-input" />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Plan</Label>
                            <Select value={planSlug} onValueChange={setPlanSlug}>
                                <SelectTrigger data-testid="grant-plan-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="free">Free</SelectItem>
                                    <SelectItem value="pro">Pro</SelectItem>
                                    <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Months</Label>
                            <Input type="number" min="1" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="grant-months-input" className="font-mono" />
                        </div>
                    </div>
                )}

                {!isComp && (
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note (optional)</Label>
                        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for the comp grant…" />
                    </div>
                )}
            </div>
            <DialogFooter>
                <Button onClick={submit} disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="grant-submit-btn">
                    {loading ? "Granting…" : isComp ? "Grant partner comp" : "Grant subscription"}
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}

function DiscountDialog({ company, onDone }) {
    const [percent, setPercent] = useState(company.discount_percent || 0);
    const [loading, setLoading] = useState(false);
    const submit = async () => {
        setLoading(true);
        try {
            await api.post(`/super-admin/companies/${company.id}/discount?percent=${parseFloat(percent)}`);
            toast.success(`${percent}% discount applied`);
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <DialogContent data-testid="discount-dialog">
            <DialogHeader>
                <DialogTitle className="font-display">Apply standing discount · {company.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Percent off (0–100)</Label>
                <Input type="number" min="0" max="100" value={percent} onChange={(e) => setPercent(e.target.value)} className="font-mono" data-testid="discount-percent-input" />
                <p className="text-xs text-muted-foreground">This applies to every future invoice for this company on top of any promo code.</p>
            </div>
            <DialogFooter>
                <Button onClick={submit} disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="discount-submit-btn">
                    {loading ? "Saving…" : "Apply discount"}
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}

function PlansTab() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const refresh = async () => setPlans((await api.get("/super-admin/plans")).data);
    useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

    const updateField = async (id, key, value) => {
        try {
            await api.patch(`/super-admin/plans/${id}`, { [key]: value });
            toast.success("Plan updated");
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        }
    };

    if (loading) return <div className="text-sm text-muted-foreground">Loading plans…</div>;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
            {plans.map((p) => (
                <div key={p.id} className="border border-border rounded-md bg-white p-5" data-testid={`plan-${p.slug}`}>
                    <div className="flex items-center justify-between">
                        <h3 className="font-display font-bold text-lg">{p.name}</h3>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{p.slug}</span>
                    </div>
                    <div className="mt-3 flex items-baseline gap-1">
                        <span className="text-3xl font-bold font-mono text-navy">{fmtMoney(p.price_monthly, p.currency)}</span>
                        <span className="text-xs text-muted-foreground">/ month</span>
                    </div>
                    <div className="mt-4 space-y-2">
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Monthly price</Label>
                        <div className="flex gap-2">
                            <Input
                                type="number"
                                defaultValue={p.price_monthly}
                                onBlur={(e) => updateField(p.id, "price_monthly", parseFloat(e.target.value))}
                                className="font-mono h-8"
                                data-testid={`plan-price-${p.slug}`}
                            />
                        </div>
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Active</Label>
                        <Select value={p.is_active ? "yes" : "no"} onValueChange={(v) => updateField(p.id, "is_active", v === "yes")}>
                            <SelectTrigger className="h-8" data-testid={`plan-active-${p.slug}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="yes">Active</SelectItem>
                                <SelectItem value="no">Disabled</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <ul className="mt-4 space-y-1.5 text-xs">
                        {(p.features || []).map((f, i) => (
                            <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-gold mt-0.5 shrink-0" />{f}</li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function DiscountsTab() {
    const [codes, setCodes] = useState([]);
    const [open, setOpen] = useState(false);
    const refresh = async () => setCodes((await api.get("/super-admin/discounts")).data);
    useEffect(() => { refresh(); }, []);

    const del = async (id) => {
        if (!confirm("Delete this discount code?")) return;
        await api.delete(`/super-admin/discounts/${id}`);
        refresh();
    };

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white gap-1.5" data-testid="new-discount-btn">
                            <Plus className="w-4 h-4" /> New discount code
                        </Button>
                    </DialogTrigger>
                    <NewDiscountDialog onDone={() => { setOpen(false); refresh(); }} />
                </Dialog>
            </div>
            {codes.length === 0 ? (
                <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">
                    No discount codes yet. Create one to share with prospects.
                </div>
            ) : (
                <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="discounts-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                <th className="text-left px-4 py-3">Code</th>
                                <th className="text-left px-4 py-3">% off</th>
                                <th className="text-left px-4 py-3">Description</th>
                                <th className="text-left px-4 py-3">Redemptions</th>
                                <th className="text-left px-4 py-3">Expires</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {codes.map((c) => (
                                <tr key={c.id} className="border-b border-border/60 last:border-0" data-testid={`discount-row-${c.code}`}>
                                    <td className="px-4 py-3 font-mono font-bold text-navy">{c.code}</td>
                                    <td className="px-4 py-3 font-mono text-gold">{c.percent_off}%</td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground">{c.description || "—"}</td>
                                    <td className="px-4 py-3 text-xs font-mono">{c.redemptions || 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Never"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <Button size="icon" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-discount-${c.code}`}>
                                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function NewDiscountDialog({ onDone }) {
    const [form, setForm] = useState({ code: "", percent_off: 10, description: "", max_redemptions: "", expires_at: "" });
    const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                code: form.code,
                percent_off: parseFloat(form.percent_off),
                description: form.description || null,
                max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
                expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
            };
            await api.post("/super-admin/discounts", payload);
            toast.success("Discount code created");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <DialogContent data-testid="new-discount-dialog">
            <DialogHeader>
                <DialogTitle className="font-display">Create discount code</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Code *</Label>
                        <Input required value={form.code} onChange={setF("code")} placeholder="LAUNCH20" className="font-mono uppercase" data-testid="discount-code-input" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Percent off *</Label>
                        <Input type="number" min="1" max="100" required value={form.percent_off} onChange={setF("percent_off")} className="font-mono" data-testid="discount-percent-off-input" />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                    <Input value={form.description} onChange={setF("description")} placeholder="Launch promo for early adopters" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Max redemptions</Label>
                        <Input type="number" value={form.max_redemptions} onChange={setF("max_redemptions")} placeholder="Unlimited" className="font-mono" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Expires</Label>
                        <Input type="date" value={form.expires_at} onChange={setF("expires_at")} />
                    </div>
                </div>
                <DialogFooter>
                    <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="submit-discount-btn">
                        {loading ? "Creating…" : "Create code"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}

function TransactionsTab() {
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/super-admin/transactions").then((r) => setTxns(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-sm text-muted-foreground">Loading transactions…</div>;
    if (!txns.length) {
        return (
            <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">
                No transactions yet. They will appear here once tenants subscribe.
            </div>
        );
    }
    return (
        <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="transactions-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                        <th className="text-left px-4 py-3">Company</th>
                        <th className="text-right px-4 py-3">Amount</th>
                        <th className="text-left px-4 py-3">Plan</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-left px-4 py-3">Discount</th>
                        <th className="text-left px-4 py-3">Date</th>
                    </tr>
                </thead>
                <tbody>
                    {txns.map((t) => (
                        <tr key={t.id} className="border-b border-border/60 last:border-0">
                            <td className="px-4 py-3">{t.company?.name || "—"}</td>
                            <td className="px-4 py-3 text-right font-mono font-medium text-navy">{fmtMoney(t.amount, t.currency)}</td>
                            <td className="px-4 py-3 capitalize text-xs">{t.plan_slug || "—"}</td>
                            <td className="px-4 py-3">
                                <Badge variant="outline" className={`text-[10px] capitalize ${t.payment_status === "paid" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : t.payment_status === "granted" ? "text-gold border-gold/30 bg-gold/10" : ""}`}>
                                    {t.payment_status || t.status}
                                </Badge>
                            </td>
                            <td className="px-4 py-3 text-xs font-mono text-gold">{t.discount_code || "—"}</td>
                            <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{fmtDateRelative(t.created_at)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── CH Sourcing Data Tab ─────────────────────────────────────────────────────

function SourcingDataTab() {
    const [month, setMonth] = useState("");          // "" = current month (backend default)
    const [running, setRunning] = useState(false);
    const [runId, setRunId] = useState(null);
    const [activeRun, setActiveRun] = useState(null);
    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(true);

    const currentMonthDefault = new Date().toISOString().slice(0, 7); // "YYYY-MM"

    const loadRuns = async () => {
        try {
            const d = await api.get("/sourcing/ingest/status");
            setRuns((d.data.runs || []).slice(0, 8));
        } catch { /* silently ignore */ }
        finally { setRunsLoading(false); }
    };

    useEffect(() => { loadRuns(); }, []);

    // Poll until active run completes
    useEffect(() => {
        if (!runId) return;
        const interval = setInterval(async () => {
            try {
                const d = await api.get("/sourcing/ingest/status");
                const run = (d.data.runs || []).find(r => r.run_id === runId);
                if (run) {
                    setActiveRun(run);
                    if (run.status === "complete" || run.status === "error") {
                        clearInterval(interval);
                        setRunning(false);
                        setRuns((d.data.runs || []).slice(0, 8));
                    }
                }
            } catch { /* ignore */ }
        }, 3000);
        return () => clearInterval(interval);
    }, [runId]);

    const handleDownload = async () => {
        if (running) return;
        setRunning(true);
        setActiveRun(null);
        try {
            const target = month || currentMonthDefault;
            const d = await api.post(`/sourcing/ingest/download-from-ch?month=${target}`);
            setRunId(d.data.run_id);
            setActiveRun({ status: "downloading", scanned_count: 0, passed_filter_count: 0 });
            toast.success(`CH bulk download started for ${target}`);
        } catch (err) {
            setRunning(false);
            const msg = err?.response?.data?.detail || err.message || "Unknown error";
            toast.error(`Download failed: ${msg}`);
        }
    };

    // Alert banner: any run with alert_required=true
    const alertedRun = runs.find(r => r.alert_required);

    return (
        <div className="space-y-6" data-testid="ch-data-tab">

            {/* ── Alert banner ─────────────────────────────────── */}
            {alertedRun && (
                <div data-testid="ch-alert-banner" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-semibold text-red-800">Monthly CH ingestion requires attention</p>
                        <p className="text-red-700 mt-0.5">
                            The scheduled run for <span className="font-mono">{alertedRun.source_url?.match(/\d{4}-\d{2}/)?.[0] || "recent month"}</span> failed
                            after all retries. Use the button below to trigger a manual run, or check{" "}
                            <a href="https://download.companieshouse.gov.uk/en_output.html" target="_blank" rel="noopener noreferrer"
                               className="underline hover:text-red-900">download.companieshouse.gov.uk</a> to confirm the file is published.
                        </p>
                    </div>
                </div>
            )}

            {/* ── Download Panel ────────────────────────────────── */}
            <div className="border border-border rounded-md bg-white">
                <div className="px-5 py-4 border-b border-border bg-muted/30 flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-sm text-navy">Companies House Bulk Data</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            The backend downloads the monthly ZIP directly from Companies House — no file
                            handling required on your end. The scheduler runs automatically on the 2nd of
                            each month; use this button for a manual run or to recover from a failed schedule.
                        </p>
                    </div>
                    <Database className="w-5 h-5 text-muted-foreground shrink-0" />
                </div>
                <div className="p-5 space-y-4">
                    {/* Month + Download row */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <label htmlFor="ch-month" className="text-sm text-muted-foreground whitespace-nowrap">Month</label>
                            <input
                                id="ch-month"
                                type="month"
                                value={month}
                                onChange={e => setMonth(e.target.value)}
                                placeholder={currentMonthDefault}
                                className="text-sm border border-border rounded-md px-3 py-1.5 bg-muted/20 font-mono w-36 focus:outline-none focus:ring-1 focus:ring-navy/40"
                                data-testid="ch-month-input"
                            />
                        </div>
                        <Button
                            onClick={handleDownload}
                            disabled={running}
                            data-testid="ch-download-btn"
                            className="bg-navy hover:bg-navy/90 text-white gap-2"
                        >
                            {running ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Running…</>
                            ) : (
                                <><Download className="w-4 h-4" /> Download &amp; Ingest</>
                            )}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                            Leave month blank to use current month ({currentMonthDefault})
                        </span>
                    </div>

                    {/* Active run progress */}
                    {activeRun && (
                        <div
                            data-testid="ch-ingest-status"
                            className={`rounded-md border px-4 py-3 text-sm space-y-1.5 ${
                                activeRun.status === "complete"
                                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                    : activeRun.status === "error"
                                    ? "bg-red-50 border-red-200 text-red-800"
                                    : "bg-amber-50 border-amber-200 text-amber-800"
                            }`}
                        >
                            <div className="flex items-center gap-2 font-medium">
                                {activeRun.status === "complete" ? (
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                ) : activeRun.status === "error" ? (
                                    <AlertCircle className="w-4 h-4 text-red-500" />
                                ) : (
                                    <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                                )}
                                {activeRun.status === "complete" ? "Ingestion complete"
                                    : activeRun.status === "downloading" ? "Downloading from Companies House…"
                                    : activeRun.status === "processing" ? "Processing CSV…"
                                    : activeRun.status === "error" ? "Ingestion failed"
                                    : "Ingestion running…"}
                            </div>
                            {activeRun.status === "complete" && (
                                <div className="grid grid-cols-4 gap-4 text-xs font-mono mt-1">
                                    <div><span className="text-muted-foreground">Scanned</span><br />{(activeRun.scanned_count || 0).toLocaleString()}</div>
                                    <div><span className="text-muted-foreground">Passed filter</span><br />{(activeRun.passed_filter_count || 0).toLocaleString()}</div>
                                    <div><span className="text-muted-foreground">Inserted</span><br />{(activeRun.inserted_count || 0).toLocaleString()}</div>
                                    <div><span className="text-muted-foreground">Updated</span><br />{(activeRun.updated_count || 0).toLocaleString()}</div>
                                </div>
                            )}
                            {activeRun.status === "processing" && activeRun.scanned_count > 0 && (
                                <p className="text-xs font-mono text-amber-700 mt-1">
                                    {(activeRun.scanned_count || 0).toLocaleString()} rows scanned · {(activeRun.passed_filter_count || 0).toLocaleString()} passed
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Run History ─────────────────────────────────── */}
            <div className="border border-border rounded-md bg-white">
                <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center justify-between">
                    <h3 className="font-semibold text-sm text-navy">Ingestion Run History</h3>
                    <button onClick={loadRuns} className="text-muted-foreground hover:text-navy transition-colors" title="Refresh" data-testid="refresh-runs-btn">
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
                {runsLoading ? (
                    <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                ) : runs.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No ingestion runs yet.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="ingestion-runs-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/20">
                                <th className="text-left px-4 py-2.5">Started</th>
                                <th className="text-left px-4 py-2.5">Type</th>
                                <th className="text-left px-4 py-2.5">Status</th>
                                <th className="text-right px-4 py-2.5">Scanned</th>
                                <th className="text-right px-4 py-2.5">Passed</th>
                                <th className="text-right px-4 py-2.5">Inserted</th>
                                <th className="text-right px-4 py-2.5">Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((r) => (
                                <tr key={r.run_id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{fmtDateRelative(r.started_at)}</td>
                                    <td className="px-4 py-2.5">
                                        <Badge variant="outline" className="text-[10px] font-mono capitalize">{r.run_type?.replace("_", " ") || "—"}</Badge>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <Badge variant="outline" className={`text-[10px] capitalize ${
                                            r.status === "complete" ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                                            : r.status === "running" ? "text-amber-700 border-amber-200 bg-amber-50"
                                            : r.status === "error" ? "text-red-700 border-red-200 bg-red-50"
                                            : "text-muted-foreground"
                                        }`}>{r.status}</Badge>
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">{(r.scanned_count || 0).toLocaleString()}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">{(r.passed_filter_count || 0).toLocaleString()}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">{(r.inserted_count || 0).toLocaleString()}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs">{(r.updated_count || 0).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
